<template>
  <base-content>
    <template v-slot:top>
      <hero-header heading="Co-funding Opportunities">
        <p class="mb-4">
          Our Co-funding opportunities allow you to choose your own loans that are specific to the project you select.
          As such, your returns and security are only related to this specific project and, if that project fails or
          gets into difficulties, no one else will be responsible for repayIng your loan or interest to you.
          We encourage you to remember the importance of diversification and spreading your capital across multiple
          loans rather than just a few. This helps to spread risk. All loan terms are specified as target projections
          only and are not guaranteed. Most loans do not hit these targets and either repay early or later than
          expected and your loan term will only end once the underlying project has completed and the borrower has repaid the loan.
          Co-funding loans are not regulated by the Financial Conduct Authority.
        </p>
        <div class="row align-items-center mt-4">
          <div class="col">
            <div class="d-flex align-items-center justify-content-start">
              <div class="mr-3">Sort by:</div>
              <base-radio name="sortBy" native-value="-createdAt" v-model="filters.sort">Newest</base-radio>
              <base-radio class="mx-3" name="sortBy" native-value="-interestRate" v-model="filters.sort">Return</base-radio>
              <base-radio name="sortBy" native-value="-term" v-model="filters.sort">Term</base-radio>
            </div>
            <base-checkbox v-model="filters.hideFunded" class="mt-2">
              Hide fully funded loans
            </base-checkbox>
          </div>
          <div class="col text-right">
            <div class="d-flex align-items-center justify-content-sm-end">
              <div class="mr-3">Show as:</div>
              <div class="btn-group mt-3 mt-sm-0">
                <base-button :active="display === 'Grid'" light icon-left="th-large" @click="display = 'Grid'">
                  Grid
                </base-button>
                <base-button :active="display === 'List'" light icon-left="list" @click="display = 'List'">
                  List
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </hero-header>
    </template>
    <template v-if="loaded">
      <template v-if="investments.length">
        <component :is="`Investment${display}`"/>
      </template>
      <template v-else>
        <p class="lead text-center">
          There are currently no opportunities available.
        </p>
      </template>
    </template>
    <template v-else>
      <base-loader />
    </template>
  </base-content>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import InvestmentGrid from '@/components/InvestmentGrid.vue'
import InvestmentList from '@/components/InvestmentList.vue'
import HeroHeader from '@/components/HeroHeader.vue'

export default {
  metaInfo: {
    title: 'Co-funding Opportunities'
  },

  components: {
    InvestmentGrid,
    InvestmentList,
    HeroHeader
  },

  data: () => ({
    display: 'Grid',
    filters: {
      sort: '-createdAt',
      projectType: {
        'Development Senior': { active: false, value: 'DevelopmentSenior' },
        'Development Mezz': { active: false, value: 'DevelopmentMezz' },
        'Development Stretch': { active: false, value: 'DevelopmentStretch' },
        'Bridging': { active: false, value: 'Bridging' },
        'Other': { active: false, value: 'Other' }
      },
      securityType: {
        'First Charge': { active: false, value: 'FirstCharge' },
        'Second Charge': { active: false, value: 'SecondCharge' },
        'Unsecured': { active: false, value: 'Unsecured' }
      },
      hideFunded: false,
      interestRate: false,
      loanTerm: false,
      paymentSchedule: {
        Monthly: { active: false, value: 'Monthly' },
        Quarterly: { active: false, value: 'Quarterly' },
        'Every Six Months': { active: false, value: 'EverySixMonths' },
        Annually: { active: false, value: 'Annually' },
        'On Redemption': { active: false, value: 'OnRedemption' }
      }
    },
    introHidden: false,
    loaded: false,
    open: false,
    options: {
      interestRate: {
        '< 6%': { active: false, operator: 'lte', value: 6 },
        '< 10%': { active: false, operator: 'lte', value: 10 },
        '< 15%': { active: false, operator: 'lte', value: 15 },
        '> 15%': { active: false, operator: 'gt', value: 15 },
        'Any': { active: true, value: false }
      },
      loanTerm: {
        '< 6 months': { active: false, operator: 'lte', value: 6 },
        '< 12 months': { active: false, operator: 'lte', value: 12 },
        '< 24 months': { active: false, operator: 'lte', value: 24 },
        '> 24 months': { active: false, operator: 'gt', value: 24 },
        'Any': { active: true, value: false }
      }
    }
  }),

  created () {
    this.request().then(() => {
      this.loaded = true
    })
  },

  computed: {
    ...mapState({
      investments: store => store.core.investments.opportunities,
      isOpportunitiesPending: store => store.core.investments.isOpportunitiesPending
    })
  },

  methods: {
    getOptions () {
      const options = {
        projectType: {},
        securityType: {},
        paymentSchedule: {},
        hideFunded: this.filters.hideFunded
      }
      const multiples = ['projectType', 'securityType', 'paymentSchedule']
      const singles = ['interestRate', 'loanTerm']

      multiples.forEach(key => {
        this.$_.forIn(this.filters[key], ({ active, value }) => {
          if (active) {
            if (!this.$_.has(options[key], '$in')) {
              options[key].$in = []
            }

            options[key].$in.push(value)
          }
        })

        if (Object.keys(options[key]).length === 0) {
          this.$_.unset(options, key)
        }
      })

      singles.forEach(key => {
        if (this.filters[key]) {
          const option = this.options[key][this.filters[key]]

          if (option.value) {
            options[key] = {}
            options[key]['$' + option.operator] = option.value
          }
        }
      })

      return options
    },

    request () {
      return this.getOpportunities(this.getOptions())
    },

    ...mapActions({
      getOpportunities: 'core/investments/opportunities'
    })
  },

  watch: {
    filters: {
      deep: true,
      handler () {
        this.request()
      }
    }
  }
}
</script>
